// Independent transport context: api.js must not import the reactive inspector (which uses api.js).
const HEADER = 'X-Rights-Preview'
let getContext = () => null
let apiBaseURL = ''

export function configureRightsPreviewTransport(options = {}) {
  if (options.getContext) getContext = options.getContext
  if (options.apiBaseURL) apiBaseURL = options.apiBaseURL
}

export function previewRequestPath(config = {}) {
  try {
    const origin = typeof window === 'undefined' ? 'http://localhost' : window.location.origin
    const base = new URL(apiBaseURL || config.baseURL || '/api/', origin)
    const requestedBase = new URL(config.baseURL || base.href, origin)
    const raw = String(config.url || '')
    const absolute = /^(?:[a-z][a-z0-9+.-]*:)?\/\//i.test(raw)
    const requested = absolute ? new URL(raw, base.href) : new URL(requestedBase.href.replace(/\/$/, '') + '/' + raw.replace(/^\//, ''))
    const prefix = base.pathname.replace(/\/$/, '') + '/'
    if (requested.origin !== base.origin || !requested.pathname.startsWith(prefix)) return null
    return requested.pathname.slice(prefix.length).replace(/\/$/, '')
  } catch (_) { return null }
}

const bypassRequest = (path, method) => (path === 'auth/me' && method === 'get')
  || (path === 'engine/rights-inspection/options' && method === 'get')
  || (path === 'engine/rights-inspection/evaluate' && method === 'post')
const backgroundPath = path => path !== null && /^(?:chat(?:\/|$)|engine\/page-usage$|engine\/sendsocket$)/.test(path)

export function previewReadAllowed(config) {
  const path = previewRequestPath(config), method = String(config.method || 'get').toLowerCase()
  if (bypassRequest(path, method)) return true
  if (path === null) return false
  if (backgroundPath(path)) return false
  if (method === 'get' && /^engine\/guides(?:\/[1-9][0-9]*)?$/.test(path)) return true
  if (method === 'get') return ['allpage', 'favorite'].includes(path)
    || /^interface\/[a-zA-Z][a-zA-Z0-9_]*\/(?:\d+\/edit|new)$/.test(path)
    || /^interface\/[a-zA-Z][a-zA-Z0-9_]*\/\d+\/[a-zA-Z][a-zA-Z0-9_]*\/\d+\/edit$/.test(path)
    || /^interface\/[a-zA-Z][a-zA-Z0-9_]*\/\d+\/[a-zA-Z][a-zA-Z0-9_]*\/new$/.test(path)
  if (method !== 'post') return false
  if (['page', 'engine/module/getfields', 'engine/module/getfieldswithjunctions', 'engine/reloadOptions', 'engine/droits/getdroit'].includes(path)) return true
  if (path !== 'displaymodule') return false
  let data = config.data || {}
  if (typeof data === 'string') { try { data = JSON.parse(data) } catch (_) { return false } }
  if (!data || typeof data !== 'object' || ['action', 'trackedmailid', 'export', 'send', 'group', 'annulsend', 'fromScenario', 'ventilation', 'onlysync', 'crop', 'delete', 'image', 'importFile', 'fileReplace'].some(key => Object.prototype.hasOwnProperty.call(data, key))) return false
  return data.operation === undefined || data.operation === null || data.operation === 'load'
}

export function captureRightsPreviewRequest(config = {}) {
  const context = getContext() || {}
  const path = previewRequestPath(config)
  if (bypassRequest(path, String(config.method || 'get').toLowerCase())) return null
  const preview = context.eligible && context.mode === 'preview' && context.target && context.target.kind === 'person'
    ? { person_id: Number(context.target.id), profile_id: Number(String(context.identity).split(':')[1]) } : null
  return { identity: context.identity || '', generation: context.viewGeneration || 0, preview }
}

function previewError(code, config, message) {
  const error = new Error(message)
  error.code = code; error.config = config; error.__CANCEL__ = true
  return error
}

export function assertRightsPreviewCurrent(config = {}) {
  const captured = config._rightsPreviewContext
  if (!captured) return
  const context = getContext() || {}
  if (captured.identity !== (context.identity || '') || captured.generation !== (context.viewGeneration || 0)) {
    throw previewError('RIGHTS_PREVIEW_STALE', config, 'Cette requête appartient à une vue précédente.')
  }
}

export function prepareRightsPreviewRequest(config) {
  if (!Object.prototype.hasOwnProperty.call(config, '_rightsPreviewContext')) config._rightsPreviewContext = captureRightsPreviewRequest(config)
  assertRightsPreviewCurrent(config)
  config.headers = config.headers || {}
  for (const key of Object.keys(config.headers)) if (key.toLowerCase() === HEADER.toLowerCase()) delete config.headers[key]
  const captured = config._rightsPreviewContext
  if (captured && captured.preview) {
    if (!previewReadAllowed(config)) throw previewError('RIGHTS_PREVIEW_READ_ONLY', config, 'Cet aperçu est en lecture seule. Revenez à votre vue pour effectuer cette action.')
    const path = previewRequestPath(config)
    if (path !== null && !backgroundPath(path)) config.headers[HEADER] = JSON.stringify(captured.preview)
  }
  return config
}

export const isRightsPreviewCancellation = error => !!error && ['RIGHTS_PREVIEW_STALE', 'RIGHTS_PREVIEW_READ_ONLY'].includes(error.code)
